<script setup>
/**
 * Team.
 *
 * One page for everyone who answers customers: the AI agents that take the
 * first pass, the teammates they hand over to, and the teams those handovers
 * are routed through. The section owns its own left column - the shell renders
 * Team rail-only, because the navigation here is the account's own people
 * rather than a fixed list of links.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/PageHeader.vue';
import CreateTeamDialog from './components/CreateTeamDialog.vue';
import InviteTeammateDialog from './components/InviteTeammateDialog.vue';
import TeamDetail from './components/TeamDetail.vue';
import TeamNavColumn from './components/TeamNavColumn.vue';
import TeammateDetail from './components/TeammateDetail.vue';
import TeammatesTable from './components/TeammatesTable.vue';
import TeamsList from './components/TeamsList.vue';

const VIEWS = {
  TEAMMATES: 'teammates',
  TEAMMATE_DETAIL: 'teammate-detail',
  TEAMS: 'teams',
  TEAM_DETAIL: 'team-detail',
};

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const assistants = useMapGetter('captainAssistants/getRecords');
const membersOfTeam = useMapGetter('teamMembers/getTeamMembers');
const currentUser = useMapGetter('getCurrentUser');
const agentUiFlags = useMapGetter('agents/getUIFlags');
const teamUiFlags = useMapGetter('teams/getUIFlags');
const teamMemberUiFlags = useMapGetter('teamMembers/getUIFlags');

const view = ref(VIEWS.TEAMMATES);
const selectedTeammateId = ref(null);
const selectedTeamId = ref(null);
const inviteDialogRef = ref(null);
const createTeamDialogRef = ref(null);

// An unconfirmed agent has been invited but has not accepted yet, which is the
// one state the availability status cannot describe.
const statusMeta = computed(() => ({
  pending: {
    label: t('TEAM_PAGE.STATUS.PENDING'),
    dotClass: 'bg-n-amber-9',
  },
  online: { label: t('TEAM_PAGE.STATUS.ONLINE'), dotClass: 'bg-n-teal-9' },
  busy: { label: t('TEAM_PAGE.STATUS.BUSY'), dotClass: 'bg-n-amber-9' },
  offline: { label: t('TEAM_PAGE.STATUS.OFFLINE'), dotClass: 'bg-n-slate-8' },
}));

const roleLabels = computed(() => ({
  administrator: t('TEAM_PAGE.ROLES.ADMINISTRATOR'),
  agent: t('TEAM_PAGE.ROLES.AGENT'),
}));

const teammates = computed(() =>
  agents.value.map(agent => {
    const status = agent.confirmed
      ? statusMeta.value[agent.availability_status] || statusMeta.value.offline
      : statusMeta.value.pending;
    const isAdministrator = agent.role === 'administrator';

    return {
      id: agent.id,
      name: agent.name,
      email: agent.email,
      thumbnail: agent.thumbnail,
      isAdministrator,
      roleLabel: isAdministrator
        ? roleLabels.value.administrator
        : roleLabels.value.agent,
      statusLabel: status.label,
      statusDotClass: status.dotClass,
    };
  })
);

const teamRows = computed(() =>
  teams.value.map(team => ({
    ...team,
    memberCount: membersOfTeam.value(team.id).length,
  }))
);

const selectedTeammate = computed(() =>
  teammates.value.find(teammate => teammate.id === selectedTeammateId.value)
);

const selectedTeam = computed(() =>
  teamRows.value.find(team => team.id === selectedTeamId.value)
);

const selectedTeamMembers = computed(() =>
  selectedTeam.value ? membersOfTeam.value(selectedTeam.value.id) : []
);

const selectedTeammateMemberships = computed(() =>
  teams.value.filter(team =>
    membersOfTeam
      .value(team.id)
      .some(member => member.id === selectedTeammateId.value)
  )
);

const selectedKey = computed(() => {
  if (view.value === VIEWS.TEAMMATE_DETAIL)
    return `teammate-${selectedTeammateId.value}`;
  if (view.value === VIEWS.TEAM_DETAIL) return `team-${selectedTeamId.value}`;
  return view.value;
});

const loadTeams = async () => {
  try {
    await store.dispatch('teams/get');
    await Promise.all(
      teams.value.map(team =>
        store.dispatch('teamMembers/get', { teamId: team.id })
      )
    );
  } catch {
    useAlert(t('TEAM_PAGE.TEAMS.LOAD_ERROR'));
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('captainAssistants/get');
  loadTeams();
});

const select = ({ view: nextView, id }) => {
  view.value = nextView;
  if (nextView === VIEWS.TEAMMATE_DETAIL) selectedTeammateId.value = id;
  if (nextView === VIEWS.TEAM_DETAIL) selectedTeamId.value = id;
};

const showTeammate = id => select({ view: VIEWS.TEAMMATE_DETAIL, id });
const showTeam = id => select({ view: VIEWS.TEAM_DETAIL, id });

const openInviteDialog = () => {
  view.value = VIEWS.TEAMMATES;
  inviteDialogRef.value?.open();
};

const inviteTeammate = async payload => {
  try {
    await store.dispatch('agents/create', payload);
    inviteDialogRef.value?.onSuccess();
    useAlert(t('TEAM_PAGE.INVITE_DIALOG.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TEAM_PAGE.INVITE_DIALOG.ERROR')
    );
  }
};

const createTeam = async ({ name, description, memberIds }) => {
  try {
    const team = await store.dispatch('teams/create', {
      name,
      description,
      allow_auto_assign: true,
    });
    await store.dispatch('teamMembers/create', {
      teamId: team.id,
      agentsList: memberIds,
    });
    await store.dispatch('teamMembers/get', { teamId: team.id });
    createTeamDialogRef.value?.onSuccess();
    useAlert(t('TEAM_PAGE.CREATE_TEAM_DIALOG.SUCCESS'));
    showTeam(team.id);
  } catch {
    useAlert(t('TEAM_PAGE.CREATE_TEAM_DIALOG.ERROR'));
  }
};

// The members endpoint takes the full desired roster rather than a delta, so
// both adding and removing send the whole list.
const setTeamMembers = async agentsList => {
  const teamId = selectedTeam.value.id;
  try {
    await store.dispatch('teamMembers/update', { teamId, agentsList });
    await store.dispatch('teamMembers/get', { teamId });
    useAlert(t('TEAM_PAGE.TEAM_DETAIL.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('TEAM_PAGE.TEAM_DETAIL.UPDATE_ERROR'));
  }
};

const addTeamMember = agentId =>
  setTeamMembers([
    ...selectedTeamMembers.value.map(member => member.id),
    agentId,
  ]);

const removeTeamMember = agentId =>
  setTeamMembers(
    selectedTeamMembers.value
      .map(member => member.id)
      .filter(id => id !== agentId)
  );

const removeTeammate = async agentId => {
  try {
    await store.dispatch('agents/delete', agentId);
    await loadTeams();
    useAlert(t('TEAM_PAGE.TEAMMATE_DETAIL.REMOVE_SUCCESS'));
    view.value = VIEWS.TEAMMATES;
  } catch {
    useAlert(t('TEAM_PAGE.TEAMMATE_DETAIL.REMOVE_ERROR'));
  }
};
</script>

<template>
  <div class="flex w-full h-full bg-n-background">
    <div
      class="hidden h-full overflow-y-auto border-e border-n-weak w-64 shrink-0 md:block"
    >
      <TeamNavColumn
        :assistants="assistants"
        :teammates="teammates"
        :teams="teamRows"
        :selected-key="selectedKey"
        @select="select"
        @invite="openInviteDialog"
      />
    </div>

    <div class="flex-1 min-w-0 px-4 py-6 overflow-y-auto md:px-6">
      <div
        v-if="view === VIEWS.TEAMMATES"
        class="flex flex-col w-full gap-5 mx-auto max-w-5xl"
      >
        <PageHeader
          :title="t('TEAM_PAGE.TEAMMATES.TITLE')"
          :subtitle="t('TEAM_PAGE.TEAMMATES.SUBTITLE')"
        >
          <template #actions>
            <Button
              color="blue"
              icon="i-lucide-plus"
              :label="t('TEAM_PAGE.TEAMMATES.INVITE')"
              @click="openInviteDialog"
            />
          </template>
        </PageHeader>
        <TeammatesTable :teammates="teammates" @select="showTeammate" />
      </div>

      <TeammateDetail
        v-else-if="view === VIEWS.TEAMMATE_DETAIL && selectedTeammate"
        :teammate="selectedTeammate"
        :memberships="selectedTeammateMemberships"
        :is-current-user="selectedTeammate.id === currentUser.id"
        :is-removing="agentUiFlags.isDeleting"
        @back="view = VIEWS.TEAMMATES"
        @remove="removeTeammate"
      />

      <div
        v-else-if="view === VIEWS.TEAMS"
        class="flex flex-col w-full gap-5 mx-auto max-w-5xl"
      >
        <PageHeader
          :title="t('TEAM_PAGE.TEAMS.TITLE')"
          :subtitle="t('TEAM_PAGE.TEAMS.SUBTITLE')"
        >
          <template #actions>
            <Button
              color="blue"
              icon="i-lucide-plus"
              :label="t('TEAM_PAGE.TEAMS.CREATE')"
              @click="createTeamDialogRef?.open()"
            />
          </template>
        </PageHeader>
        <TeamsList :teams="teamRows" @select="showTeam" />
      </div>

      <TeamDetail
        v-else-if="view === VIEWS.TEAM_DETAIL && selectedTeam"
        :team="selectedTeam"
        :members="selectedTeamMembers"
        :teammates="teammates"
        :is-updating="teamMemberUiFlags.isUpdating"
        @back="view = VIEWS.TEAMS"
        @add-member="addTeamMember"
        @remove-member="removeTeamMember"
      />
    </div>

    <InviteTeammateDialog
      ref="inviteDialogRef"
      :is-loading="agentUiFlags.isCreating"
      @invite="inviteTeammate"
    />
    <CreateTeamDialog
      ref="createTeamDialogRef"
      :teammates="teammates"
      :is-loading="teamUiFlags.isCreating"
      @create="createTeam"
    />
  </div>
</template>
